/**
 * 全站狀態 Badge 色票／icon（同一 status 全站同一組）。
 * 淺底、無框、深字 semibold；Fill icon 走 currentColor。
 * DRAFT 禁止 PiNotePencilFill（與編輯操作區隔）。
 * RECEIVED 無 PiInboxFill → PiTrayArrowDownFill。
 */

import {
  PiAirplaneTakeoffFill,
  PiArchiveFill,
  PiArrowUUpLeftFill,
  PiCheckCircleFill,
  PiCircleHalfFill,
  PiCurrencyCircleDollarFill,
  PiFileTextFill,
  PiGearFill,
  PiHandPalmFill,
  PiHandshakeFill,
  PiHourglassFill,
  PiHouseFill,
  PiPackageFill,
  PiPaperPlaneRightFill,
  PiPaperPlaneTiltFill,
  PiProhibitFill,
  PiReceiptFill,
  PiSealCheckFill,
  PiTrayArrowDownFill,
  PiTrayFill,
  PiTruckFill,
  PiWarningFill,
  PiWarningOctagonFill,
  PiXCircleFill,
} from 'react-icons/pi';

export const STATUS_BADGE_SIZE = 'h-7 px-2.5 text-sm';

export const STATUS_BADGE_ICON_SLOT = '[&>svg]:size-3.5! [&>svg]:fill-current';

export const STATUS_TONE_FALLBACK = 'text-slate-800 bg-slate-100 font-semibold border-0';

export const STATUS_TONE = {
  DRAFT: 'text-slate-800 bg-olive-200 font-semibold',
  SUBMITTED: 'text-sky-800 bg-sky-200 font-semibold',
  ISSUED: 'text-indigo-800 bg-indigo-100 font-semibold border-0',
  COMPLETED: 'text-emerald-800 bg-emerald-200 font-semibold border-0',
  READY: 'text-emerald-800 bg-emerald-200 font-semibold border-0',
  APPROVED: 'text-amber-800 bg-amber-100 font-semibold border-0',
  REJECTED: 'text-rose-800 bg-rose-100 font-semibold border-0',
  REFUSED: 'text-rose-800 bg-rose-100 font-semibold border-0',
  CONFIRMED: 'text-indigo-800 bg-indigo-100 font-semibold border-0',
  ESTABLISHED: 'text-indigo-800 bg-indigo-100 font-semibold border-0',
  IN_PROGRESS: 'text-amber-800 bg-amber-100 font-semibold border-0',
  PROCESSING: 'text-amber-800 bg-amber-100 font-semibold border-0',
  PREPARING: 'text-slate-800 bg-slate-200 font-semibold border-0',
  SHIPPING: 'text-sky-800 bg-sky-100 font-semibold border-0',
  PARTIALLY_SHIPPED: 'text-violet-800 bg-violet-100 font-semibold border-0',
  SHIPPED: 'text-teal-800 bg-teal-100 font-semibold border-0',
  DELIVERED: 'text-teal-800 bg-teal-100 font-semibold border-0',
  INVOICED: 'text-violet-800 bg-violet-100 font-semibold border-0',
  PAID: 'text-emerald-800 bg-emerald-100 font-semibold border-0',
  PARTIALLY_RECEIVED: 'text-violet-800 bg-violet-100 font-semibold border-0',
  RECEIVED: 'text-teal-800 bg-teal-100 font-semibold border-0',
  CLOSED: 'text-slate-800 bg-slate-200 font-semibold border-0',
  CANCELLED: 'text-rose-900 bg-rose-200 font-semibold border-0',
  TERMINATED: 'text-rose-900 bg-rose-100 font-semibold border-0',
  ABORTED: 'text-rose-900 bg-rose-100 font-semibold border-0',
  RISK: 'text-amber-800 bg-amber-100 font-semibold border-0',
  BLOCKED: 'text-rose-800 bg-rose-100 font-semibold border-0',
  DATA_ERROR: 'text-amber-800 bg-amber-200 font-semibold border-0',
  FINANCE_PROCESSING: 'text-amber-800 bg-amber-100 font-semibold border-0',
  RETURNED: 'text-rose-800 bg-rose-100 font-semibold border-0',
  ACTIVE: 'text-emerald-800 bg-emerald-200 font-semibold border-0',
  INACTIVE: 'text-red-800 bg-red-200 font-semibold border-0',
  PENDING_ASSIGNMENT: 'text-amber-800 bg-amber-100 font-semibold border-0',
  PENDING: 'text-amber-800 bg-amber-100 font-semibold border-0',
  REPAIRED: 'text-emerald-800 bg-emerald-200 font-semibold border-0',
};

/** 可點時：同色更深淺底、維持深字（禁止 hover:text-white） */
export const STATUS_TONE_INTERACTIVE = {
  DRAFT: 'hover:bg-slate-200 focus-visible:bg-slate-200',
  SUBMITTED: 'hover:bg-sky-200 focus-visible:bg-sky-200',
  ISSUED: 'hover:bg-indigo-200 focus-visible:bg-indigo-200',
  COMPLETED: 'hover:bg-emerald-300 focus-visible:bg-emerald-300',
  READY: 'hover:bg-emerald-300 focus-visible:bg-emerald-300',
  APPROVED: 'hover:bg-amber-200 focus-visible:bg-amber-200',
  REJECTED: 'hover:bg-rose-200 focus-visible:bg-rose-200',
  REFUSED: 'hover:bg-rose-200 focus-visible:bg-rose-200',
  CONFIRMED: 'hover:bg-indigo-200 focus-visible:bg-indigo-200',
  ESTABLISHED: 'hover:bg-indigo-200 focus-visible:bg-indigo-200',
  IN_PROGRESS: 'hover:bg-amber-200 focus-visible:bg-amber-200',
  PROCESSING: 'hover:bg-amber-200 focus-visible:bg-amber-200',
  PREPARING: 'hover:bg-slate-300 focus-visible:bg-slate-300',
  SHIPPING: 'hover:bg-sky-200 focus-visible:bg-sky-200',
  PARTIALLY_SHIPPED: 'hover:bg-violet-200 focus-visible:bg-violet-200',
  SHIPPED: 'hover:bg-teal-200 focus-visible:bg-teal-200',
  DELIVERED: 'hover:bg-teal-200 focus-visible:bg-teal-200',
  INVOICED: 'hover:bg-violet-200 focus-visible:bg-violet-200',
  PAID: 'hover:bg-emerald-200 focus-visible:bg-emerald-200',
  PARTIALLY_RECEIVED: 'hover:bg-violet-200 focus-visible:bg-violet-200',
  RECEIVED: 'hover:bg-teal-200 focus-visible:bg-teal-200',
  CLOSED: 'hover:bg-slate-300 focus-visible:bg-slate-300',
  CANCELLED: 'hover:bg-rose-300 focus-visible:bg-rose-300',
  TERMINATED: 'hover:bg-rose-200 focus-visible:bg-rose-200',
  ABORTED: 'hover:bg-rose-200 focus-visible:bg-rose-200',
  RISK: 'hover:bg-amber-200 focus-visible:bg-amber-200',
  BLOCKED: 'hover:bg-rose-200 focus-visible:bg-rose-200',
  DATA_ERROR: 'hover:bg-amber-300 focus-visible:bg-amber-300',
  FINANCE_PROCESSING: 'hover:bg-amber-200 focus-visible:bg-amber-200',
  RETURNED: 'hover:bg-rose-200 focus-visible:bg-rose-200',
  ACTIVE: 'hover:bg-emerald-300 focus-visible:bg-emerald-300',
  INACTIVE: 'hover:bg-red-300 focus-visible:bg-red-300',
  PENDING_ASSIGNMENT: 'hover:bg-amber-200 focus-visible:bg-amber-200',
  PENDING: 'hover:bg-amber-200 focus-visible:bg-amber-200',
  REPAIRED: 'hover:bg-emerald-300 focus-visible:bg-emerald-300',
};

export const STATUS_TONE_INTERACTIVE_FALLBACK =
  'hover:bg-slate-200 focus-visible:bg-slate-200';

export const STATUS_ICON = {
  DRAFT: PiFileTextFill,
  SUBMITTED: PiPaperPlaneTiltFill,
  ISSUED: PiPaperPlaneRightFill,
  COMPLETED: PiCheckCircleFill,
  READY: PiCheckCircleFill,
  APPROVED: PiSealCheckFill,
  REJECTED: PiArrowUUpLeftFill,
  REFUSED: PiProhibitFill,
  CONFIRMED: PiHandshakeFill,
  ESTABLISHED: PiHandshakeFill,
  IN_PROGRESS: PiGearFill,
  PROCESSING: PiGearFill,
  PREPARING: PiPackageFill,
  SHIPPING: PiTruckFill,
  PARTIALLY_SHIPPED: PiCircleHalfFill,
  SHIPPED: PiAirplaneTakeoffFill,
  DELIVERED: PiHouseFill,
  INVOICED: PiReceiptFill,
  PAID: PiCurrencyCircleDollarFill,
  PARTIALLY_RECEIVED: PiTrayFill,
  RECEIVED: PiTrayArrowDownFill,
  CLOSED: PiArchiveFill,
  CANCELLED: PiXCircleFill,
  TERMINATED: PiProhibitFill,
  ABORTED: PiProhibitFill,
  RISK: PiWarningFill,
  BLOCKED: PiHandPalmFill,
  DATA_ERROR: PiWarningOctagonFill,
  FINANCE_PROCESSING: PiGearFill,
  RETURNED: PiArrowUUpLeftFill,
  ACTIVE: PiCheckCircleFill,
  INACTIVE: PiProhibitFill,
  PENDING_ASSIGNMENT: PiHourglassFill,
  PENDING: PiHourglassFill,
  REPAIRED: PiCheckCircleFill,
};
